import { Package, Clock, Wrench, Ban, CheckCircle, Truck, AlertTriangle } from 'lucide-react';

/**
 * Dock Status Badge Component
 * Displays color-coded status badges for different dock operational states
 */
const DockStatusBadge = ({ detailedStatus, size = 'md', showDescription = false }) => {
  if (!detailedStatus) {
    return null;
  }

  const { code, label, description } = detailedStatus;

  // Define colors and icons for each status
  const statusConfig = {
    // Available - Green
    AVAILABLE: {
      color: 'bg-green-100 text-green-800 border-green-300',
      icon: CheckCircle,
      iconColor: 'text-green-600'
    },

    // Ready to Unload - Blue
    READY_TO_UNLOAD: {
      color: 'bg-blue-100 text-blue-800 border-blue-300',
      icon: Truck,
      iconColor: 'text-blue-600'
    },

    // Truck Assigned - Cyan
    TRUCK_ASSIGNED: {
      color: 'bg-cyan-100 text-cyan-800 border-cyan-300',
      icon: Truck,
      iconColor: 'text-cyan-600'
    },

    // Unloading in Progress - Amber
    UNLOADING_IN_PROGRESS: {
      color: 'bg-amber-100 text-amber-800 border-amber-300',
      icon: Package,
      iconColor: 'text-amber-600'
    },

    // Unloading Completed - Purple (Ready to Release)
    UNLOADING_COMPLETED: {
      color: 'bg-purple-100 text-purple-800 border-purple-300',
      icon: CheckCircle,
      iconColor: 'text-purple-600'
    },

    // Unloading Halted - Orange/Warning
    UNLOADING_HALTED: {
      color: 'bg-orange-100 text-orange-800 border-orange-300',
      icon: AlertTriangle,
      iconColor: 'text-orange-600'
    },

    // Occupied - Gray
    OCCUPIED: {
      color: 'bg-gray-100 text-gray-800 border-gray-300',
      icon: Package,
      iconColor: 'text-gray-600'
    },

    // Maintenance - Yellow
    MAINTENANCE: {
      color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      icon: Wrench,
      iconColor: 'text-yellow-600'
    },

    // Blocked - Red
    BLOCKED: {
      color: 'bg-red-100 text-red-800 border-red-300',
      icon: Ban,
      iconColor: 'text-red-600'
    }
  };

  const config = statusConfig[code] || statusConfig.OCCUPIED;
  const Icon = config.icon;

  // Size classes
  const sizeClasses = {
    sm: {
      container: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3'
    },
    md: {
      container: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4'
    },
    lg: {
      container: 'px-4 py-2 text-base',
      icon: 'w-5 h-5'
    }
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;

  if (showDescription) {
    return (
      <div className="space-y-1">
        <div className={`inline-flex items-center rounded-full border font-medium ${config.color} ${sizeClass.container}`}>
          <Icon className={`${sizeClass.icon} mr-1.5 ${config.iconColor}`} />
          {label}
        </div>
        <p className="text-xs text-gray-600 italic">{description}</p>
      </div>
    );
  }

  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${config.color} ${sizeClass.container}`}
      title={description}
    >
      <Icon className={`${sizeClass.icon} mr-1.5 ${config.iconColor}`} />
      {label}
    </span>
  );
};

export default DockStatusBadge;
